import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Store,
  RefreshCw,
  ChevronRight,
  Search,
  Plus,
  Check,
  AlertCircle,
  Trash2,
  Phone,
  Mail,
  UserCheck,
  Users,
  UserPlus,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Portal } from '../types';
import { SlidePanel } from '../components/SlidePanel';
import {
  CREATE_RETAILER,
  GET_CUSTOM_RETAILERS,
  GET_FSRS,
  GET_RETAILERS,
  SET_FSR_RETAILERS,
} from '../graphql/queries';
import { useMyStore } from '../hooks/useMyStore';

interface LiveFsr {
  id: string;
  name: string;
  employeeId: string;
  avatar: string | null;
  color: string | null;
  assignedRetailerIds: string[];
}

interface LiveRetailer {
  id: string;
  name: string;
  area: string;
  email: string | null;
  mobile: string | null;
  status: string | null;
  // Local-only assignment overlay
  assignedFsrId?: string | null;
  assignedFsrName?: string | null;
}

const initialsOf = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

interface Props {
  portal: Portal;
  setTitle: (t: string) => void;
  setSubtitle: (s: string) => void;
  showToast: (m: string, t?: any) => void;
}

export const RetailerAssignment: React.FC<Props> = ({ portal, setTitle, setSubtitle, showToast }) => {
  useEffect(() => {
    setTitle('Retailer Assignment');
    setSubtitle(
      portal === 'manufacturer'
        ? 'Manage direct store assignments and field visibility'
        : 'Assign stores to your delivery & sales team',
    );
  }, [setTitle, setSubtitle, portal]);

  const PAGE_SIZE = 50;

  const { store: myStore } = useMyStore();
  const isCustom = myStore?.storeType === 'custom';

  const fsrQuery = useQuery(GET_FSRS, {
    variables: { page: 1, limit: 200 },
    fetchPolicy: 'cache-and-network',
  });

  // RiEtail stores read retailers from the upstream connector. Custom stores
  // read their locally-managed Retailer collection.
  const retailerQuery = useQuery(GET_RETAILERS, {
    variables: { page: 1, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: isCustom,
  });
  const customRetailerQuery = useQuery(GET_CUSTOM_RETAILERS, {
    variables: { pageNo: 0, limit: 500 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !isCustom,
  });

  const [loadedPage, setLoadedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (retailerQuery.data?.getRetailers?.page) {
      setLoadedPage(retailerQuery.data.getRetailers.page);
    }
  }, [retailerQuery.data?.getRetailers?.page]);

  const loadMoreRetailers = async () => {
    if (isCustom) return;
    const total = retailerQuery.data?.getRetailers?.total ?? 0;
    if (loadingMore) return;
    if ((retailerQuery.data?.getRetailers?.customers?.length ?? 0) >= total) return;
    setLoadingMore(true);
    try {
      const next = loadedPage + 1;
      await retailerQuery.fetchMore({
        variables: { page: next, limit: PAGE_SIZE },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.getRetailers) return prev;
          const merged = [
            ...(prev?.getRetailers?.customers ?? []),
            ...fetchMoreResult.getRetailers.customers,
          ];
          const seen = new Set<string>();
          const customers = merged.filter((c: any) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
          return {
            getRetailers: {
              ...fetchMoreResult.getRetailers,
              customers,
            },
          };
        },
      });
      setLoadedPage(next);
    } finally {
      setLoadingMore(false);
    }
  };

  const fsrs: LiveFsr[] = (fsrQuery.data?.getFsrs?.fsrs ?? []).map((f: any) => ({
    id: f.id,
    name: f.name,
    employeeId: f.employeeId,
    avatar: f.avatar,
    color: f.color,
    assignedRetailerIds: (f.assignedRetailers ?? []).map((a: any) => a.retailerId),
  }));

  const remoteRetailers: LiveRetailer[] = useMemo(() => {
    if (isCustom) {
      return (customRetailerQuery.data?.getCustomRetailers?.items ?? []).map((c: any) => ({
        id: c.id,
        name: c.name || 'Unnamed',
        area: c.city || '—',
        email: c.emailId || null,
        mobile: c.mobileNumber || null,
        status: c.status ? 'active' : 'inactive',
      })) as LiveRetailer[];
    }
    return (retailerQuery.data?.getRetailers?.customers ?? []).map((c: any) => {
      const display =
        c.displayName ||
        c.companyName ||
        `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ||
        c.email ||
        'Unnamed';
      return {
        id: c.id,
        name: display,
        area: c.city || '—',
        email: c.email,
        mobile: c.mobile,
        status: c.status,
      } as LiveRetailer;
    });
  }, [isCustom, retailerQuery.data, customRetailerQuery.data]);


  // retailerId → fsrId reverse-index built from each FSR's assignedRetailerIds
  const assignmentIndex = useMemo(() => {
    const idx: Record<string, string> = {};
    fsrs.forEach((f) => {
      (f.assignedRetailerIds || []).forEach((rid) => {
        idx[rid] = f.id;
      });
    });
    return idx;
  }, [fsrs]);

  const retailers: LiveRetailer[] = remoteRetailers.map((r) => {
    const fsrId = assignmentIndex[r.id];
    if (!fsrId) return r;
    const fsr = fsrs.find((f) => f.id === fsrId);
    return { ...r, assignedFsrId: fsr?.id ?? null, assignedFsrName: fsr?.name ?? null };
  });

  const [setFsrRetailersMutation, { loading: saving }] = useMutation(SET_FSR_RETAILERS);

  const [selectedFSRId, setSelectedFSRId] = useState<string | null>(null);
  const [fsrSearch, setFsrSearch] = useState('');
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [tempRetailerIds, setTempRetailerIds] = useState<string[]>([]);
  const [showAddRetailer, setShowAddRetailer] = useState(false);

  const selectedFSR = fsrs.find((f) => f.id === selectedFSRId) ?? null;
  const currentAssignedRetailers = retailers.filter((r) => r.assignedFsrId === selectedFSRId);

  const refetchRetailers = () => {
    if (isCustom) {
      customRetailerQuery.refetch({ pageNo: 0, limit: 500 });
    } else {
      setLoadedPage(1);
      retailerQuery.refetch({ page: 1, limit: PAGE_SIZE });
    }
  };

  const handleSync = () => {
    showToast('Refreshing retailer list…', 'info');
    refetchRetailers();
    fsrQuery.refetch();
  };

  const openAssignPanel = () => {
    if (!selectedFSRId) return;
    const myCurrent = fsrs.find((f) => f.id === selectedFSRId)?.assignedRetailerIds ?? [];
    setTempRetailerIds(myCurrent);
    setShowAssignPanel(true);
  };

  const toggleRetailer = (id: string) =>
    setTempRetailerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const saveAssignments = async () => {
    if (!selectedFSRId) return;
    try {
      const { data } = await setFsrRetailersMutation({
        variables: { fsrId: selectedFSRId, retailerIds: tempRetailerIds },
      });
      const out = data?.setFsrRetailers;
      if (out?.error) {
        showToast(out.message || 'Failed to save assignments', 'error');
        return;
      }
      await fsrQuery.refetch();
      setShowAssignPanel(false);
      showToast(`Direct assignments updated for ${selectedFSR?.name}`, 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to save assignments', 'error');
    }
  };

  const removeAssignment = async (retId: string) => {
    if (!selectedFSRId) return;
    const myCurrent = fsrs.find((f) => f.id === selectedFSRId)?.assignedRetailerIds ?? [];
    const remaining = myCurrent.filter((id) => id !== retId);
    try {
      const { data } = await setFsrRetailersMutation({
        variables: { fsrId: selectedFSRId, retailerIds: remaining },
      });
      if (data?.setFsrRetailers?.error) {
        showToast(data.setFsrRetailers.message || 'Failed to remove', 'error');
        return;
      }
      await fsrQuery.refetch();
    } catch (err: any) {
      showToast(err?.message || 'Failed to remove', 'error');
    }
  };

  const activeQuery = isCustom ? customRetailerQuery : retailerQuery;
  const loading = activeQuery.loading && remoteRetailers.length === 0;
  const apiError = isCustom
    ? customRetailerQuery.data?.getCustomRetailers?.error
      ? customRetailerQuery.data.getCustomRetailers.message
      : customRetailerQuery.error?.message || null
    : retailerQuery.data?.getRetailers?.error
      ? retailerQuery.data.getRetailers.message
      : retailerQuery.error?.message || null;

  return (
    <div className="space-y-6 page-fade-in pb-10">
      {/* ADD RETAILER */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => setShowAddRetailer(true)}
          className="h-12 px-6 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl flex items-center gap-2 text-[13px] font-black shadow-lg hover:shadow-emerald-200 uppercase tracking-widest active:scale-95 transition-all"
        >
          <UserPlus size={18} strokeWidth={3} /> Add Retailer
        </button>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: 'Direct Partnerships',
            value: retailers.filter((r) => r.assignedFsrId).length,
            icon: Store,
            color: 'from-blue-600 to-blue-700',
          },
          {
            label: isCustom ? 'Saved Retailers' : 'Synced Stores',
            value: remoteRetailers.length,
            icon: RefreshCw,
            color: 'from-indigo-600 to-indigo-700',
          },
          {
            label: 'Active Direct FSRs',
            value: fsrs.filter((u) => retailers.some((r) => r.assignedFsrId === u.id)).length,
            icon: UserCheck,
            color: 'from-emerald-600 to-emerald-700',
          },
          {
            label: 'Unassigned',
            value: remoteRetailers.length - retailers.filter((r) => r.assignedFsrId).length,
            icon: AlertCircle,
            color: 'from-amber-600 to-amber-700',
          },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-3xl border border-white shadow-sm flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}
            >
              <stat.icon size={22} strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">
                {stat.label}
              </p>
              <div className="text-2xl font-black text-slate-900 tracking-tight leading-none">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>Couldn't load retailers{isCustom ? '' : ' from B2B'}: {apiError}</span>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 h-[700px]">
        {/* FSR LIST */}
        <div className="col-span-4 bg-white border border-white rounded-[32px] shadow-xl flex flex-col overflow-hidden">
          <div className="p-6 border-b border-slate-50">
            <h3 className="text-lg font-black text-slate-900 tracking-tight mb-4 flex items-center gap-2">
              <Users size={20} className="text-indigo-600" /> Key Account Officers
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search officers..."
                className="w-full h-11 pl-10 pr-4 bg-slate-50 border-2 border-transparent rounded-2xl outline-none focus:border-indigo-500/30 text-[13px] font-bold transition-all shadow-inner"
                value={fsrSearch}
                onChange={(e) => setFsrSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2.5">
            {fsrQuery.loading && fsrs.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-12">Loading FSRs…</div>
            ) : fsrs.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-12">
                No field users yet. Add one in Field Users first.
              </div>
            ) : (
              fsrs
                .filter((f) => f.name.toLowerCase().includes(fsrSearch.toLowerCase()))
                .map((fsr) => {
                  const isSelected = selectedFSRId === fsr.id;
                  const directCount = retailers.filter((r) => r.assignedFsrId === fsr.id).length;
                  return (
                    <button
                      key={fsr.id}
                      onClick={() => setSelectedFSRId(fsr.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all group
                        ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-200' : 'bg-white border-transparent hover:bg-slate-50 text-slate-900'}`}
                    >
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-sm shadow-lg"
                        style={{ backgroundColor: fsr.color || '#4F46E5' }}
                      >
                        {fsr.avatar || initialsOf(fsr.name)}
                      </div>
                      <div className="flex-1 text-left">
                        <div
                          className={`text-[15px] font-black tracking-tight leading-none mb-1 ${
                            isSelected ? 'text-white' : 'text-slate-900'
                          }`}
                        >
                          {fsr.name}
                        </div>
                        <div className={`text-[11px] font-bold ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                          {directCount} Direct {directCount === 1 ? 'Store' : 'Stores'} · {fsr.employeeId}
                        </div>
                      </div>
                      <ChevronRight size={18} className={isSelected ? 'text-white/50' : 'text-slate-300'} />
                    </button>
                  );
                })
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-span-8 bg-white border border-white rounded-[32px] shadow-xl flex flex-col overflow-hidden relative">
          <AnimatePresence mode="wait">
            {selectedFSR ? (
              <motion.div
                key={`fsr-${selectedFSR.id}`}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex flex-col h-full"
              >
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
                  <div className="flex items-center gap-5">
                    <div
                      className="w-16 h-16 rounded-3xl flex items-center justify-center text-white font-black text-2xl shadow-xl"
                      style={{ backgroundColor: selectedFSR.color || '#4F46E5' }}
                    >
                      {selectedFSR.avatar || initialsOf(selectedFSR.name)}
                    </div>
                    <div>
                      <div className="text-[11px] font-black text-indigo-600 uppercase tracking-[0.25em] mb-1">
                        Direct Mapping for
                      </div>
                      <h2 className="text-3xl font-black text-slate-900 tracking-tighter italic leading-none">
                        {selectedFSR.name}
                      </h2>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSync}
                      className="h-12 px-5 bg-white border-2 border-slate-100 rounded-2xl flex items-center gap-2 text-[13px] font-black text-slate-600 hover:border-indigo-100 uppercase tracking-widest transition-all"
                    >
                      <RefreshCw size={18} className={activeQuery.loading || fsrQuery.loading ? 'animate-spin' : ''} /> Refresh
                    </button>
                    <button
                      onClick={openAssignPanel}
                      className="h-12 px-6 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl flex items-center gap-2 text-[13px] font-black shadow-lg hover:shadow-indigo-200 uppercase tracking-widest active:scale-95 transition-all"
                    >
                      <Plus size={18} strokeWidth={3} /> Assign Direct Stores
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                  {currentAssignedRetailers.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-6 border-4 border-dashed border-slate-200">
                        <Store size={48} />
                      </div>
                      <h4 className="text-xl font-black text-slate-900 tracking-tight italic">No Direct Relationships</h4>
                      <p className="text-[14px] font-medium text-slate-500 max-w-xs mt-2 italic leading-relaxed">
                        This officer doesn't have any direct retailer assignments yet. Use the button to map key accounts.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {currentAssignedRetailers.map((ret) => (
                        <div
                          key={ret.id}
                          className="bg-white border-2 border-slate-50 rounded-[32px] p-6 shadow-sm hover:shadow-xl hover:border-emerald-50 transition-all group overflow-hidden relative"
                        >
                          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50/30 rounded-full -mr-8 -mt-8" />
                          <div className="flex items-center gap-4 mb-6">
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg group-hover:rotate-6 transition-transform bg-emerald-500">
                              {ret.name[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-[11px] font-black text-emerald-600 uppercase tracking-wider mb-1 opacity-70 italic">
                                {ret.id.slice(-6).toUpperCase()}
                              </div>
                              <h4 className="text-lg font-black text-slate-900 truncate leading-none italic">{ret.name}</h4>
                            </div>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-[12px] text-slate-600">
                              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
                                <Mail size={13} />
                              </div>
                              <span className="font-semibold truncate">{ret.email || <span className="text-slate-300 italic">No email</span>}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[12px] text-slate-600">
                              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0">
                                <Phone size={13} />
                              </div>
                              <span className="font-semibold">{ret.mobile || <span className="text-slate-300 italic">No mobile</span>}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                            {ret.status ? (
                              <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-emerald-50 border-emerald-100 text-emerald-600">
                                {ret.status}
                              </span>
                            ) : <span />}
                            <div className="flex gap-2">
                              {ret.mobile && (
                                <a
                                  href={`tel:${ret.mobile}`}
                                  className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 hover:border-indigo-100 transition-all"
                                >
                                  <Phone size={16} />
                                </a>
                              )}
                              <button
                                onClick={() => removeAssignment(ret.id)}
                                className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 hover:border-rose-100 transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-20 opacity-40">
                <div className="w-32 h-32 bg-slate-50 rounded-[40px] flex items-center justify-center text-slate-300 mb-8 border-4 border-dashed border-slate-100 rotate-12">
                  <Store size={64} />
                </div>
                <h3 className="text-[28px] font-black text-slate-900 tracking-tighter italic">Select an Officer</h3>
                <p className="text-[16px] font-medium text-slate-500 max-w-sm mt-3 italic leading-relaxed">
                  Choose a Key Account Manager to manage their direct store relationships and visit schedules.
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ASSIGN SLIDE PANEL */}
      <SlidePanel
        isOpen={showAssignPanel}
        onClose={() => setShowAssignPanel(false)}
        width={580}
        title="Direct Store Selection"
        subtitle={`Select retailers for ${selectedFSR?.name ?? ''}`}
      >
        <div className="space-y-8 pb-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Search across ${remoteRetailers.length} ${isCustom ? 'saved' : 'synced'} stores...`}
              className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-transparent rounded-[24px] outline-none focus:border-emerald-500/30 text-[15px] font-bold transition-all shadow-inner italic"
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
            />
          </div>

          <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-2 pt-2">
            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] mb-4 pl-1">
              {isCustom ? 'YOUR SAVED RETAILERS' : 'AVAILABLE B2B STORES'}
            </h4>
            {loading ? (
              <div className="text-center text-slate-400 text-sm py-12">Loading retailers…</div>
            ) : (
              retailers
                .filter((r) => r.name.toLowerCase().includes(assignSearch.toLowerCase()))
                .map((ret) => {
                  const isAssignedToOther = ret.assignedFsrId && ret.assignedFsrId !== selectedFSRId;
                  const isSelected = tempRetailerIds.includes(ret.id);
                  return (
                    <button
                      key={ret.id}
                      disabled={!!isAssignedToOther}
                      onClick={() => toggleRetailer(ret.id)}
                      className={`w-full flex items-center gap-4 p-5 rounded-[32px] border-2 transition-all relative overflow-hidden group
                        ${isSelected ? 'bg-emerald-600 border-emerald-600 text-white shadow-xl' : 'bg-white border-slate-50 hover:border-emerald-100 hover:bg-slate-50'}
                        ${isAssignedToOther ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                    >
                      <div
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg
                          ${isSelected ? 'bg-white/20' : 'bg-slate-100 text-slate-400'}`}
                      >
                        {ret.name[0].toUpperCase()}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[15px] font-black tracking-tight italic ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                            {ret.name}
                          </span>
                          {isAssignedToOther && (
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-[8px] font-black rounded uppercase">
                              {ret.assignedFsrName}
                            </span>
                          )}
                        </div>
                        <div className={`text-[11px] font-bold flex flex-col gap-1 ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                          {ret.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone size={10} /> {ret.mobile}
                            </span>
                          )}
                          {ret.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail size={10} /> <span className="truncate">{ret.email}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`w-7 h-7 rounded-full border-3 flex items-center justify-center transition-all
                          ${isSelected ? 'bg-white border-white' : 'bg-white border-slate-100 shadow-inner'}`}
                      >
                        {isSelected && <Check size={16} className="text-emerald-600" strokeWidth={4} />}
                      </div>
                    </button>
                  );
                })
            )}

            {!loading && !isCustom && (() => {
              const total = retailerQuery.data?.getRetailers?.total ?? 0;
              const loaded = remoteRetailers.length;
              if (loaded >= total) return null;
              return (
                <div className="pt-4 flex flex-col items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400">
                    Showing {loaded} of {total}
                  </span>
                  <button
                    onClick={loadMoreRetailers}
                    disabled={loadingMore}
                    className="h-11 px-6 rounded-full border-2 border-emerald-100 bg-white text-emerald-600 font-black text-[12px] uppercase tracking-widest hover:bg-emerald-50 transition disabled:opacity-60 flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" /> Loading…
                      </>
                    ) : (
                      <>Show more ({Math.min(PAGE_SIZE, total - loaded)})</>
                    )}
                  </button>
                </div>
              );
            })()}
          </div>

          <div className="flex gap-4 pt-4 sticky bottom-0 bg-white pb-10">
            <button
              onClick={() => setShowAssignPanel(false)}
              className="flex-1 h-14 bg-slate-100 text-slate-500 font-black text-[15px] rounded-2xl uppercase tracking-widest hover:bg-slate-200 transition-all italic"
            >
              Cancel
            </button>
            <button
              onClick={saveAssignments}
              disabled={saving}
              className="flex-[2] h-14 rounded-2xl font-black text-[15px] uppercase tracking-widest shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3 italic bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-emerald-200 disabled:opacity-60"
            >
              {saving ? 'Saving…' : (<>Commit Assignments <Check size={20} strokeWidth={3} /></>)}
            </button>
          </div>
        </div>
      </SlidePanel>

      {showAddRetailer && (
        <AddRetailerModal
          isCustom={isCustom}
          onClose={() => setShowAddRetailer(false)}
          onCreated={(msg) => {
            setShowAddRetailer(false);
            showToast(msg, 'success');
            refetchRetailers();
          }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}
    </div>
  );
};

/**
 * Collects the basics a retailer needs and hands them to `createRetailer`.
 * The backend resolves the RiEtail user type from the `storefront_only`
 * access level before creating the customer, so nothing here needs to know
 * about user types.
 */
const AddRetailerModal: React.FC<{
  isCustom: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
  onError: (message: string) => void;
}> = ({ isCustom, onClose, onCreated, onError }) => {
  const [form, setForm] = useState({ name: '', email: '', mobile: '' });
  const [createRetailer, { loading }] = useMutation(CREATE_RETAILER);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      mobile: form.mobile.trim() || null,
    };
    if (!input.name) {
      onError('Retailer name is required');
      return;
    }
    if (!input.email && !input.mobile) {
      onError('Enter a mobile number or an email');
      return;
    }
    try {
      const { data } = await createRetailer({ variables: { input } });
      const res = data?.createRetailer;
      if (res?.error) {
        onError(res.message || 'Failed to create retailer');
        return;
      }
      onCreated(res?.message || 'Retailer created');
    } catch (err: any) {
      onError(err?.message || 'Failed to create retailer');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
      <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-7 py-5 border-b border-slate-50">
          <div>
            <div className="text-[11px] font-black text-emerald-600 uppercase tracking-[0.25em] mb-1">
              New Retailer
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter italic leading-none">
              Add Retailer
            </h2>
            <p className="text-[11px] font-bold text-slate-400 mt-2">
              {isCustom
                ? 'Saved to your retailer directory.'
                : 'Created in RiEtail B2B as a storefront user.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </header>

        <form onSubmit={submit} className="p-7 space-y-4">
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Retailer Name *
            </label>
            <input
              autoFocus
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sharma Stores"
              className="mt-2 w-full py-3 px-4 bg-slate-50 border-2 border-transparent rounded-2xl outline-none focus:border-emerald-500/30 text-[15px] font-bold transition-all shadow-inner"
            />
          </div>
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Mobile Number
            </label>
            <input
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              placeholder="9876543210"
              inputMode="tel"
              className="mt-2 w-full py-3 px-4 bg-slate-50 border-2 border-transparent rounded-2xl outline-none focus:border-emerald-500/30 text-[15px] font-bold transition-all shadow-inner"
            />
          </div>
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="owner@sharmastores.com"
              className="mt-2 w-full py-3 px-4 bg-slate-50 border-2 border-transparent rounded-2xl outline-none focus:border-emerald-500/30 text-[15px] font-bold transition-all shadow-inner"
            />
          </div>
          <p className="text-[11px] font-bold text-slate-400 leading-relaxed">
            At least one of mobile or email is required — it's what the retailer
            signs in with.
          </p>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-4 bg-slate-100 text-slate-500 font-black text-[14px] rounded-2xl uppercase tracking-widest hover:bg-slate-200 transition-all disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-[2] py-4 rounded-2xl font-black text-[14px] uppercase tracking-widest shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-emerald-200 disabled:opacity-60"
            >
              {loading ? 'Creating…' : (<>Create Retailer <Check size={18} strokeWidth={3} /></>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
